<template>
    <div class="monthly-attendance-calendar">
        <Card>
            <template #title>
                <div class="flex justify-content-between align-items-center">
                    <h2>Calendario Presenze Mensile</h2>
                    <div class="flex gap-3 align-items-center">
                        <SelectButton v-model="viewMode" :options="viewOptions" optionLabel="label" optionValue="value" />
                        <Button icon="pi pi-chevron-left" @click="previousMonth" class="p-button-text" />
                        <span class="text-xl font-bold">{{ currentMonthLabel }}</span>
                        <Button icon="pi pi-chevron-right" @click="nextMonth" class="p-button-text" />
                    </div>
                </div>
            </template>
            <template #content>
                <div class="calendar-container">
                    <table class="attendance-table">
                        <thead>
                            <tr>
                                <th class="sticky-col">Corso/Staff</th>
                                <th v-for="day in daysInMonth" :key="day" class="day-header">
                                    {{ day }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="row in attendanceRows" :key="row.id">
                                <td class="sticky-col row-label">{{ row.name }}</td>
                                <td v-for="day in daysInMonth" :key="day" class="attendance-cell">
                                    <div v-if="getAttendanceForDay(row.id, day)" class="attendance-indicator">
                                        <i class="pi pi-check text-green-500"></i>
                                        <span class="attendance-count">{{ getAttendanceForDay(row.id, day) }}</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </Card>
    </div>
</template>

<script>
import { ref, computed, onMounted, getCurrentInstance } from 'vue';
import Service from '@/services/levante-api/levante-api';
import moment from 'moment';

export default {
    name: 'MonthlyAttendanceCalendar',
    setup() {
        const self = getCurrentInstance();
        self.$eventBus = self.appContext.config.globalProperties.$eventBus;
        const service = new Service(self.$eventBus);
        
        const currentDate = ref(moment());
        const attendanceData = ref([]);
        const viewMode = ref('courses');
        const viewOptions = ref([
            { label: 'Corsi', value: 'courses' },
            { label: 'Staff', value: 'staff' }
        ]);
        
        const currentMonthLabel = computed(() => {
            return currentDate.value.format('MMMM YYYY');
        });
        
        const daysInMonth = computed(() => {
            const days = [];
            const daysCount = currentDate.value.daysInMonth();
            for (let i = 1; i <= daysCount; i++) {
                days.push(i);
            }
            return days;
        });
        
        const attendanceRows = computed(() => {
            // Transform attendance data into rows based on view mode
            const rows = [];
            
            if (attendanceData.value && attendanceData.value.length > 0) {
                const grouped = {};
                
                // Filter data based on view mode
                const filteredData = attendanceData.value.filter(item => {
                    if (viewMode.value === 'courses') {
                        return item.type === 'athlete';
                    } else {
                        return item.type === 'staff';
                    }
                });
                
                filteredData.forEach(item => {
                    let key, name;
                    
                    if (viewMode.value === 'courses') {
                        // Group by course name for athlete view
                        key = item.corso_nome || 'Unknown';
                        name = item.corso_nome;
                    } else {
                        // Group by staff name for staff view - show only staff name
                        key = item.staff_nome || 'Unknown';
                        name = item.staff_nome || 'Unknown';
                    }
                    
                    if (!grouped[key]) {
                        grouped[key] = {
                            id: key,
                            name: name,
                            attendances: {}
                        };
                    }
                    
                    // Extract day from data_inizio
                    const day = moment(item.data_inizio).date();
                    if (!grouped[key].attendances[day]) {
                        grouped[key].attendances[day] = 0;
                    }
                    
                    // Add presenze count for athletes, or 1 for staff presence
                    if (item.type === 'athlete') {
                        grouped[key].attendances[day] += item.presenze || 0;
                    } else if (item.type === 'staff' && item.status === 'Presente') {
                        grouped[key].attendances[day] += 1;
                    }
                });
                
                rows.push(...Object.values(grouped));
            }
            
            return rows;
        });
        
        const getAttendanceForDay = (rowId, day) => {
            const row = attendanceRows.value.find(r => r.id === rowId);
            return row?.attendances[day] || null;
        };
        
        const loadAttendanceData = async () => {
            try {
                const month = currentDate.value.month() + 1; // moment months are 0-indexed
                const year = currentDate.value.year();
                
                console.log('Loading attendance for:', month, year);
                
                // Get athlete attendance details for the month
                const athleteRes = await service.getAthleteAttendanceDetails({
                    month: month,
                    year: year
                });
                
                console.log('Athlete attendance response:', athleteRes);
                
                // Get staff attendance details for the month
                const staffRes = await service.getStaffAttendanceDetails({
                    month: month,
                    year: year
                });
                
                console.log('Staff attendance response:', staffRes);
                
                // Combine both into attendanceData
                const allData = [];
                
                if (athleteRes && (athleteRes.item || athleteRes.items || Array.isArray(athleteRes))) {
                    const athleteData = athleteRes.item || athleteRes.items || athleteRes;
                    if (Array.isArray(athleteData)) {
                        console.log('Athlete data sample:', athleteData[0]);
                        allData.push(...athleteData.map(item => ({
                            ...item,
                            type: 'athlete'
                        })));
                    }
                }
                
                if (staffRes && (staffRes.item || staffRes.items || Array.isArray(staffRes))) {
                    const staffData = staffRes.item || staffRes.items || staffRes;
                    if (Array.isArray(staffData)) {
                        console.log('Staff data sample:', staffData[0]);
                        allData.push(...staffData.map(item => ({
                            ...item,
                            type: 'staff'
                        })));
                    }
                }
                
                attendanceData.value = allData;
                console.log('Combined attendance data length:', attendanceData.value.length);
                if (attendanceData.value.length > 0) {
                    console.log('First combined item:', attendanceData.value[0]);
                }
                console.log('attendanceRows computed:', attendanceRows.value);
            } catch (error) {
                console.error('Error loading attendance data:', error);
            }
        };
        
        const previousMonth = () => {
            currentDate.value = moment(currentDate.value).subtract(1, 'month');
            loadAttendanceData();
        };
        
        const nextMonth = () => {
            currentDate.value = moment(currentDate.value).add(1, 'month');
            loadAttendanceData();
        };
        
        onMounted(() => {
            loadAttendanceData();
        });
        
        return {
            currentMonthLabel,
            daysInMonth,
            attendanceRows,
            getAttendanceForDay,
            previousMonth,
            nextMonth,
            viewMode,
            viewOptions
        };
    }
};
</script>

<style scoped>
.monthly-attendance-calendar {
    padding: 1rem;
}

.calendar-container {
    overflow-x: auto;
    max-width: 100%;
}

.attendance-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 800px;
}

.attendance-table th,
.attendance-table td {
    border: 1px solid #e0e0e0;
    padding: 0.5rem;
    text-align: center;
}

.attendance-table th {
    background-color: #f8f9fa;
    font-weight: 600;
    position: sticky;
    top: 0;
    z-index: 10;
}

.day-header {
    min-width: 40px;
    font-size: 0.9rem;
}

.sticky-col {
    position: sticky;
    left: 0;
    background-color: #fff;
    z-index: 5;
    font-weight: 600;
    min-width: 150px;
    text-align: left !important;
}

.attendance-table thead .sticky-col {
    z-index: 15;
    background-color: #f8f9fa;
}

.row-label {
    background-color: #f8f9fa;
}

.attendance-cell {
    min-width: 40px;
    height: 40px;
    vertical-align: middle;
}

.attendance-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 2px;
}

.attendance-count {
    font-size: 0.75rem;
    font-weight: 600;
    color: #495057;
}
</style>
